import { format, isWithinInterval, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import type { Expense, ExpenseCategory } from '@/lib/types';
import { CATEGORY_LABELS } from '@/lib/types';
import { categoryColor } from '@/lib/designAdapters';

export interface CategoryAggregate {
  category: ExpenseCategory;
  label: string;
  color: string;
  total: number;
}

export interface TopItem {
  name: string;
  category: ExpenseCategory;
  qty: number;
  total: number;
}

export interface ReportsViewModel {
  byCategory: CategoryAggregate[];
  categoryTotal: number;
  status: {
    pending: number;
    reimbursed: number;
    donated: number;
    pendingCount: number;
    reimbursedCount: number;
    donatedCount: number;
    total: number;
  };
  monthlyTrend: Array<{ label: string; value: number }>;
  topItems: TopItem[];
  receiptCount: number;
}

export function buildReportsViewModel(expenses: Expense[], now = new Date()): ReportsViewModel {
  const catMap = new Map<ExpenseCategory, number>();
  const itemMap = new Map<string, TopItem>();

  for (const expense of expenses) {
    for (const item of expense.items) {
      catMap.set(item.category, (catMap.get(item.category) || 0) + item.total);
      const existing = itemMap.get(item.name);
      if (existing) {
        existing.qty += item.quantity;
        existing.total += item.total;
      } else {
        itemMap.set(item.name, { name: item.name, category: item.category, qty: item.quantity, total: item.total });
      }
    }
  }

  const byCategory = Array.from(catMap.entries())
    .map(([category, total]) => ({ category, label: CATEGORY_LABELS[category], color: categoryColor(category), total: Number(total.toFixed(2)) }))
    .sort((a, b) => b.total - a.total);

  const categoryTotal = byCategory.reduce((sum, c) => sum + c.total, 0);

  const sumBy = (predicate: (e: Expense) => boolean) =>
    expenses.filter(predicate).reduce((sum, e) => sum + e.total, 0);

  const status = {
    pending: sumBy((e) => e.status === 'pending'),
    reimbursed: sumBy((e) => e.status === 'reimbursed'),
    donated: sumBy((e) => e.status === 'donation'),
    pendingCount: expenses.filter((e) => e.status === 'pending').length,
    reimbursedCount: expenses.filter((e) => e.status === 'reimbursed').length,
    donatedCount: expenses.filter((e) => e.status === 'donation').length,
    total: 0,
  };
  status.total = status.pending + status.reimbursed + status.donated;

  const monthlyTrend = Array.from({ length: 12 }, (_, index) => {
    const monthBase = subMonths(now, 11 - index);
    const start = startOfMonth(monthBase);
    const end = endOfMonth(monthBase);
    const value = expenses
      .filter((e) => isWithinInterval(new Date(e.transaction_date), { start, end }))
      .reduce((sum, e) => sum + e.total, 0);
    return { label: format(monthBase, 'MMM'), value: Number(value.toFixed(2)) };
  });

  const topItems = Array.from(itemMap.values())
    .sort((a, b) => b.total - a.total)
    .slice(0, 8);

  return { byCategory, categoryTotal, status, monthlyTrend, topItems, receiptCount: expenses.length };
}
